import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, ArrowRight, Download, X } from 'lucide-react';
import { Button } from '@monksflow/monks-ui';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';
import { downloadFile } from '@/shared/utils/downloadFile';
import type { AssetFile } from '../../assetDetail.types';
import { FullscreenAssetControls } from './FullscreenAssetControls';
import { useTranslation } from 'react-i18next';

interface Props {
  file: AssetFile;
  siblingFiles: ProjectFileResponseDto[];
  projectId: string;
  folderId: string;
  onClose: () => void;
}

/**
 * Top chrome rendered above the portaled preview when fullscreen is active.
 * Layout: prev/next nav (left) | type-specific asset controls (center) |
 * download + close (right). Navigation preserves the current URL search
 * params (e.g. ?status=, ?version=) so filters survive across siblings;
 * fullscreen state is shared via the comments store and persists too.
 */
export function FullscreenTopBar({ file, siblingFiles, projectId, folderId, onClose }: Props) {
  const { t } = useTranslation('projects');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const currentIndex = siblingFiles.findIndex((f) => f.id === file.id);
  const prevFile = currentIndex > 0 ? siblingFiles[currentIndex - 1] : null;
  const nextFile =
    currentIndex >= 0 && currentIndex < siblingFiles.length - 1
      ? siblingFiles[currentIndex + 1]
      : null;

  const goTo = (id: string) => {
    const search = searchParams.toString();
    const path = ROUTES.PROJECT_FILE_DETAIL(projectId, folderId, id);
    void navigate(search ? `${path}?${search}` : path);
  };

  // Empty siblings = no surface to navigate to (e.g. the compare view passes
  // [] because the two panes share a single file). Keep the flex-1 spacer so
  // the centered asset controls stay centered.
  const showNavigation = siblingFiles.length > 0;

  // S3 returns presigned URLs cross-origin, where the anchor's `download`
  // attribute is ignored and `target="_blank"` opens the asset inline.
  // Fetch the bytes via `downloadFile` so the browser always triggers a save.
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownload = async () => {
    if (!file.downloadUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFile({
        url: file.downloadUrl,
        mimeType: file.fileType,
        fileName: file.fileName,
      });
    } catch {
      notificationService.showError(t('projectDetail.fullscreenTopBar.failedToDownloadFile'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-4 px-6 py-3">
      <div className="flex flex-1 items-center justify-start gap-2">
        {showNavigation && (
          <>
            <Button
              variant="outline"
              size="mini"
              className="size-9.5 bg-white p-0 shadow-sm"
              aria-label={t('projectDetail.fullscreenTopBar.previousAsset')}
              disabled={!prevFile}
              onClick={() => prevFile && goTo(prevFile.id)}
            >
              <ArrowLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="mini"
              className="size-9.5 bg-white p-0 shadow-sm"
              aria-label={t('projectDetail.fullscreenTopBar.nextAsset')}
              disabled={!nextFile}
              onClick={() => nextFile && goTo(nextFile.id)}
            >
              <ArrowRight className="size-4" />
            </Button>
          </>
        )}
      </div>

      <FullscreenAssetControls file={file} />

      <div className="flex flex-1 items-center justify-end gap-2">
        {file.downloadUrl && (
          <Button
            variant="default"
            size="lg"
            disabled={isDownloading}
            onClick={() => void handleDownload()}
          >
            {t('projectDetail.fullscreenTopBar.download')}
            <Download className="size-4" />
          </Button>
        )}
        <Button
          variant="outline"
          size="default"
          className="size-9.5 bg-white p-0 shadow-sm"
          aria-label={t('projectDetail.fullscreenTopBar.closeFullscreen')}
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
}
